/**
 * Os desenhos que uma região pode adotar.
 *
 * <p>Antes toda região aparecia com o mesmo triângulo — o ícone da coleção, não dela.
 * Numa lista de sete, isso obriga a ler os nomes para achar qual é qual; com um sol,
 * uma árvore e uma montanha, a forma faz o trabalho antes do texto.
 *
 * <p>São silhuetas simples de propósito: aparecem tanto grandes, no card da listagem,
 * quanto a 14px, na divisória de Áreas e Locais. Detalhe fino some no menor dos dois.
 *
 * <p>A cor não vive aqui: o ícone é desenhado na cor que a região tem no mapa. Assim
 * há uma fonte só para a identidade visual dela.
 */
export const ICONES_DE_REGIAO = [
  { id: 'sol', rotulo: 'Sol' },
  { id: 'arvore', rotulo: 'Árvore' },
  { id: 'montanha', rotulo: 'Montanha' },
  { id: 'lago', rotulo: 'Lago' },
  { id: 'pedra', rotulo: 'Pedra' },
  { id: 'libelula', rotulo: 'Libélula' },
  { id: 'ossada', rotulo: 'Ossada' },
  { id: 'ruina', rotulo: 'Ruína' },
  { id: 'fogo', rotulo: 'Fogo' },
  { id: 'onda', rotulo: 'Onda' },
  { id: 'duna', rotulo: 'Duna' },
  { id: 'cristal', rotulo: 'Cristal' },
] as const;

interface Props {
  icone?: string | null;
  cor?: string | null;
  tamanho?: number;
  titulo?: string;
}

export function IconeDeRegiao({ icone, cor, tamanho = 24, titulo }: Props) {
  return (
    <svg
      width={tamanho}
      height={tamanho}
      viewBox="0 0 24 24"
      fill="none"
      stroke={cor || 'var(--gold)'}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role="img"
      aria-label={titulo ?? icone ?? 'região'}
    >
      {titulo && <title>{titulo}</title>}
      {desenho(icone)}
    </svg>
  );
}

function desenho(icone?: string | null) {
  switch (icone) {
    case 'sol':
      return (
        <>
          <circle cx="12" cy="12" r="4.5" />
          <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M19.1 4.9l-1.8 1.8M6.7 17.3l-1.8 1.8" />
        </>
      );

    case 'arvore':
      return (
        <>
          <path d="M12 3l5 7h-3l4 6H6l4-6H7z" />
          <path d="M12 16v5" />
        </>
      );

    case 'montanha':
      return (
        <>
          <path d="M2 20l7-12 4 6.5 2.5-3.5L22 20z" />
          <path d="M7.5 11.5l3 2" />
        </>
      );

    case 'lago':
      return (
        <>
          <path d="M3 13c3-4 6-4 9 0s6 4 9 0" />
          <path d="M3 18c3-4 6-4 9 0s6 4 9 0" />
          <path d="M9 7c1-2 3-3 5-2" />
        </>
      );

    case 'pedra':
      return (
        <>
          <path d="M4 16l3-8 6-3 7 5-2 8H6z" />
          <path d="M7 8l6 2 5-2M13 10l-1 9" />
        </>
      );

    case 'libelula':
      return (
        <>
          <path d="M12 4v16" />
          <path d="M12 8C8 5 4 6 3 8c1 2 5 3 9 1" />
          <path d="M12 8c4-3 8-2 9 0-1 2-5 3-9 1" />
          <path d="M12 14c-3-2-6-1-7 1 1 1.5 4 2 7 .5" />
          <path d="M12 14c3-2 6-1 7 1-1 1.5-4 2-7 .5" />
        </>
      );

    case 'ossada':
      return (
        <>
          <path d="M9 3.5a2.5 2.5 0 013 0 2.5 2.5 0 013 0 2.5 2.5 0 010 4l-3 2-3-2a2.5 2.5 0 010-4z" />
          <path d="M12 9.5v11" />
          <path d="M8 12.5h8M8.5 16h7" />
        </>
      );

    case 'ruina':
      return (
        <>
          <path d="M4 21V9l3-2 3 2v5h4V7l3-2 3 2v14" />
          <path d="M4 21h16" />
        </>
      );

    case 'fogo':
      return (
        <>
          <path d="M12 2c3 4 6 6 6 10a6 6 0 11-12 0c0-2 1-3.5 2.5-5C9 9 10 7 12 2z" />
          <path d="M12 20a3 3 0 01-1.5-5.5C11 16 12 17 12 20z" />
        </>
      );

    case 'onda':
      return (
        <>
          <path d="M2 16c3-5 6-5 9 0s6 5 11-1" />
          <path d="M2 20c3-3 6-3 9 0s6 3 11-1" />
        </>
      );

    case 'duna':
      return (
        <>
          <path d="M2 19c4 0 5-6 9-6s5 6 11 6" />
          <circle cx="17" cy="7" r="3" />
        </>
      );

    case 'cristal':
      return (
        <>
          <path d="M12 2l6 7-6 13-6-13z" />
          <path d="M6 9h12M12 2v20" />
        </>
      );

    // Sem escolha: o triângulo da coleção, como era antes.
    default:
      return <path d="M12 4l8 15H4z" />;
  }
}
